import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, Navigate } from "react-router-dom";
import { toast } from "react-toastify";

import DistributionReviewApi from "../../../../infrastructure/api/distributionReview";
import { canAccessDistributionPortal } from "../../../utils/authRoleHelpers";
import DistributionCompanySelect, {
  getSelectedDistributionCompanyId,
  isSystemAdminContext,
} from "../DistributionCompanySelect";
import EDCBreadcrumbs, { EDC_ROOT_CRUMB } from "../EDCBreadcrumbs";
import { REQUEST_UNITS, REQUEST_VIEWS, requestViewPath } from "../edcPaths";

const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj?.[k] != null) return obj[k];
  }
  return undefined;
};

const hasUnitAccess = (context, unitKey) => {
  if (!context) return true;
  if (pick(context, "isCompanyAdmin", "IsCompanyAdmin") === true) return true;
  return unitKey === "engineering"
    ? pick(context, "canEngineeringReview", "CanEngineeringReview") === true
    : pick(context, "canAreaReview", "CanAreaReview") === true;
};

const formatDate = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString("fa-IR");
  } catch {
    return null;
  }
};

const DistributionRequestsPage = ({ unitKey, viewKey }) => {
  const unit = REQUEST_UNITS[unitKey];
  const view = REQUEST_VIEWS.find((v) => v.key === viewKey) || REQUEST_VIEWS[0];
  const canDecide = view.key === "NewRequests";

  const [companyId, setCompanyId] = useState(getSelectedDistributionCompanyId);
  const [context, setContext] = useState(null);
  const [rows, setRows] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [decisionRow, setDecisionRow] = useState(null);
  const [approve, setApprove] = useState(true);
  const [note, setNote] = useState("");
  const [capacityKw, setCapacityKw] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const ctxRes = await DistributionReviewApi.getPortalContext(companyId);
      const ctx = ctxRes?.data ?? ctxRes?.Data ?? null;
      setContext(ctx);
      if (!hasUnitAccess(ctx, unitKey)) {
        setRows([]);
        return;
      }

      const queueRes = await DistributionReviewApi.searchPortalQueue({
        tab: unit.tabs[view.key],
        page: 1,
        pageSize: 100,
        distributionCompanyId: isSystemAdminContext(ctx)
          ? pick(ctx, "distributionCompanyId", "DistributionCompanyId") ?? null
          : null,
      });
      const data = queueRes?.data ?? queueRes?.Data ?? {};
      setRows(pick(data, "items", "Items") || []);
      setNewCount(
        unitKey === "engineering"
          ? pick(data, "engineeringQueueCount", "EngineeringQueueCount") ?? 0
          : pick(data, "areaQueueCount", "AreaQueueCount") ?? 0
      );
    } catch (e) {
      setError(e?.message || "خطا در دریافت درخواست ها");
    } finally {
      setLoading(false);
    }
  }, [companyId, unit, unitKey, view.key]);

  useEffect(() => {
    load();
  }, [load]);

  const openDecision = (row, nextApprove) => {
    setDecisionRow(row);
    setApprove(nextApprove);
    setNote("");
    setCapacityKw("");
  };

  const submitDecision = async () => {
    if (!decisionRow) return;
    if (!approve && !String(note || "").trim()) {
      toast.error("دلیل رد الزامی است");
      return;
    }
    setSaving(true);
    try {
      const body = {
        requestId: pick(decisionRow, "requestInfoId", "RequestInfoId"),
        approve,
        note,
        approvedCapacityKw: capacityKw ? Number(capacityKw) : null,
      };
      if (unitKey === "engineering") await DistributionReviewApi.decideEngineering(body);
      else await DistributionReviewApi.decideArea(body);
      toast.success(approve ? "تایید ثبت شد" : "رد ثبت شد");
      setDecisionRow(null);
      await load();
    } catch (e) {
      toast.error(e?.message || "خطا در ثبت تصمیم");
    } finally {
      setSaving(false);
    }
  };

  if (!canAccessDistributionPortal()) return <Navigate to="/403" replace />;

  const unitDenied = context && !hasUnitAccess(context, unitKey);
  const decisionNoteFor = (row) =>
    unitKey === "engineering"
      ? pick(row, "engineeringDecisionNote", "EngineeringDecisionNote")
      : pick(row, "areaDecisionNote", "AreaDecisionNote");

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <EDCBreadcrumbs
          crumbs={[
            EDC_ROOT_CRUMB,
            { title: unit.title, to: requestViewPath(unitKey) },
            { title: view.title, to: requestViewPath(unitKey, view.key) },
          ]}
        />
      </Grid>

      <Grid item xs={12}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {unit.title} — {view.title}
            </Typography>
            {context ? (
              <Typography variant="body2" color="text.secondary">
                {pick(context, "distributionCompanyName", "DistributionCompanyName") || "—"}
              </Typography>
            ) : null}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <DistributionCompanySelect context={context} onChange={setCompanyId} />
            <Button variant="outlined" size="small" onClick={load} disabled={loading}>
              به‌روزرسانی
            </Button>
          </Stack>
        </Stack>
      </Grid>

      <Grid item xs={12}>
        <Tabs value={view.key} variant="scrollable" allowScrollButtonsMobile>
          {REQUEST_VIEWS.map((v) => (
            <Tab
              key={v.key}
              value={v.key}
              component={RouterLink}
              to={requestViewPath(unitKey, v.key)}
              label={v.key === "NewRequests" ? `${v.title} (${newCount})` : v.title}
            />
          ))}
        </Tabs>
      </Grid>

      {error ? (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      ) : null}

      {unitDenied ? (
        <Grid item xs={12}>
          <Alert severity="warning">
            دسترسی {unit.title} برای شما تعریف نشده است. برای دریافت دسترسی با مدیر شرکت توزیع
            تماس بگیرید.
          </Alert>
        </Grid>
      ) : (
        <Grid item xs={12}>
          {loading ? <Typography>در حال بارگذاری…</Typography> : null}
          <Stack spacing={1.5}>
            {rows.map((row) => {
              const id = pick(row, "id", "Id");
              const ref = pick(row, "referenceId", "ReferenceId");
              const decidedNote = decisionNoteFor(row);
              const updatedAt = formatDate(pick(row, "updateOn", "UpdateOn", "createOn", "CreateOn"));
              return (
                <Box
                  key={id}
                  sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box>
                      <Typography fontWeight={600}>
                        PRJ-{ref} — {pick(row, "applicantName", "ApplicantName") || "—"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        امور: {pick(row, "distributionAreaName", "DistributionAreaName") || "—"}
                        {pick(row, "cityName", "CityName")
                          ? ` · شهر: ${pick(row, "cityName", "CityName")}`
                          : ""}
                        {updatedAt ? ` · ${updatedAt}` : ""}
                      </Typography>
                      <Chip
                        size="small"
                        label={pick(row, "stageTitle", "StageTitle") || "—"}
                        sx={{ mt: 0.5 }}
                      />
                      {!canDecide && decidedNote ? (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          توضیح: {decidedNote}
                        </Typography>
                      ) : null}
                    </Box>
                    {canDecide ? (
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => openDecision(row, true)}
                        >
                          تایید
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => openDecision(row, false)}
                        >
                          رد
                        </Button>
                      </Stack>
                    ) : null}
                  </Stack>
                </Box>
              );
            })}
            {!loading && rows.length === 0 ? (
              <Typography color="text.secondary">موردی نیست.</Typography>
            ) : null}
          </Stack>
        </Grid>
      )}

      <Dialog open={Boolean(decisionRow)} onClose={() => setDecisionRow(null)} fullWidth maxWidth="sm">
        <DialogTitle>{approve ? "تایید درخواست" : "رد درخواست"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={approve ? "توضیح (اختیاری)" : "دلیل رد"}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              minRows={3}
              required={!approve}
              fullWidth
            />
            {unitKey === "engineering" && approve ? (
              <TextField
                label="ظرفیت تاییدشده مهندسی (کیلووات)"
                value={capacityKw}
                onChange={(e) => setCapacityKw(e.target.value)}
                fullWidth
              />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecisionRow(null)}>انصراف</Button>
          <Button variant="contained" onClick={submitDecision} disabled={saving}>
            ثبت
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default DistributionRequestsPage;
